/**
 * Blossom — product-recommendations.js
 * <product-recommendations data-url="…&section_id=…" [data-target="#id"] [data-hide-section="true"]>
 * Lazy-loads Shopify product recommendations (related or complementary) when
 * the element approaches the viewport, using the Section Rendering API.
 * Used by sections/recommendations.liquid and the main product
 * "Complementary products" block. Hides itself (or its section) when Shopify
 * returns no products or the request fails.
 */
(() => {
  if (!window.Theme || customElements.get('product-recommendations')) return;

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.loaded || !this.dataset.url) return;
      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    get selector() {
      return this.dataset.target || '[data-recommendations-content]';
    }

    async load() {
      if (this.loaded) return;
      this.loaded = true;
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(this.dataset.url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = Theme.parseHTML(await response.text());
        const source = doc.querySelector(this.selector);
        const target = this.querySelector(this.selector);
        if (!source || !target || !source.querySelector('[data-product-card]')) {
          this.hideEmpty();
          return;
        }
        target.innerHTML = source.innerHTML;
        this.classList.add('is-loaded');
        Theme.initReveal?.(this);
        this.dispatchEvent(new CustomEvent('recommendations:loaded', { bubbles: true }));
      } catch (error) {
        // Recommendations are optional: log for developers, hide for shoppers.
        console.warn('[product-recommendations] Could not load recommendations.', error);
        this.hideEmpty();
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    hideEmpty() {
      if (Theme.designMode) return;
      if (this.dataset.hideSection === 'true') {
        const section = this.closest('.shopify-section') || this.closest('section');
        if (section) section.hidden = true;
      }
      this.hidden = true;
    }
  }

  customElements.define('product-recommendations', ProductRecommendations);
})();
